/* eslint-disable no-underscore-dangle */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Route, Switch, withRouter } from 'react-router-dom';
import { string } from 'prop-types';
import { PlayersProvider } from './context/players';
import { MainPlayerContext, MainPlayerProvider } from './context/mainPlayer';
import Controller from './components/contenedores/MainController';
import widgetTypes from './widgetTypes';
import BannerApp from './components/contenedores/BannerApp';
import { navigate, getDeviceOS, sendHit, isNoticia } from './utils';
import { AdsProvider } from './context/ads';
import Analytics from './components/contenedores/Analytics';
import { historyPropType } from './utils/constants';
import ErrorBoundary from './components/contenedores/ErrorBoundary';
import WidgetSticky from './components/organismos/Header/contenedores/WidgetSticky';
import WidgetMenuToggle from './components/organismos/Header/contenedores/WidgetMenuToggle';
import FooterWidget from './components/organismos/Footer/widget';
import AdsController from './components/organismos/Advertisment/AdsController';
import IntexAds from './components/organismos/Advertisment/intexAds';
import Refresh from './utils/refresh';
import ScrollPages from './views/ScrollPages';

if (process.env.NODE_ENV === 'development') {
  if (document.body.classList.contains('t2')) {
    // eslint-disable-next-line no-unused-expressions
    import('./styles/webMelodia.scss');
  } else {
    // eslint-disable-next-line no-unused-expressions
    import('./styles/webEuropa.scss');
  }
}

const Header = React.lazy(() =>
  import('./components/organismos/Header/componentes')
);
const Footer = React.lazy(() => import('./components/organismos/Footer'));

const Examples = React.lazy(() => import('./Examples'));
const Galleries = React.lazy(() => import('./Galleries'));
const MainPlayer = React.lazy(() =>
  import('./components/contenedores/MainPlayer')
);

const BackendPage = ({ html }) =>
  html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : '';

BackendPage.propTypes = {
  html: string.isRequired
};

const App = ({ history }) => {
  const [html, setHtml] = useState('');

  // Usamos esta varaible para saber si borrar el primer html que nos llega del back.
  // Solo lo borramos si la url por la que se entra a la app no es renderizada por React, para evitar errores al borrar nodos del DOM de React
  const deleteMain = useRef(true);

  // Mantenemos registro sobre la url actual y la anterior
  const urlsRef = useRef({ current: window.location.href, prev: '' });

  const main = useRef(document.querySelector('main'));

  const handleNavigate = useCallback(
    (href, doPush = true, isScrollPage = false) => {
      const link = href;

      // Prueba de cambio de html del main por uno obtenido por AJAX
      return navigate(link)
        .then(data => {
          if (data) {
            // Si no es una url de Rediseño navegamos normal
            const isRD = data.htmlString
              ? data.htmlString.includes('<atres-widget')
              : false;
            if (!isRD) {
              window.location.assign(link);
              return null;
            }

            // Borrado del html inicial que llega del back para que las proximas paginas las renderice React
            if (deleteMain.current && !isScrollPage) {
              main.current.innerHTML = '';
              deleteMain.current = false;
            }

            if (data.mainHTML) {
              if (!isScrollPage) {
                setHtml(data.mainHTML);
              }
            } else {
              setHtml('');
            }

            if (!isScrollPage) {
              // Actualizamos title de la pagina
              if (data.doc) {
                document.title = data.doc.title;
              }
            }

            if (doPush && !isScrollPage) {
              const completeUrl = `${data.url.pathname}${data.url.hash}`;
              // Si hay basename lo quitamos de la url antes de hacer push para que no se duplique, ya que el react router tiene el basename seteado
              const urlToPush = process.env.REACT_APP_ROUTER_BASENAME
                ? completeUrl.replace(process.env.REACT_APP_ROUTER_BASENAME, '')
                : completeUrl;

              urlsRef.current.prev = window.location.href;
              urlsRef.current.current = link;

              const isCurrentNoticia = isNoticia();
              history.push(urlToPush, {
                isScrollPage,
                isNoticia: isCurrentNoticia
              });
            }
            if (!isScrollPage) {
              window.scrollTo(0, 0);
            }
          }
          return data;
        })
        .catch(e => {
          console.log('Error al navegar: ', e);
          window.location.assign(link);
        });
    },
    [history]
  );

  // Control de navegacion con botones del navegador
  const handleBack = useCallback(() => {
    const url = window.location.href;
    const curr = new URL(url).pathname;
    const prev = new URL(urlsRef.current.current).pathname;
    const pageHasChange = curr !== prev;
    if (pageHasChange) {
      const currurl =
        process.env.NODE_ENV === 'development'
          ? `https://www.ondacero.es${curr}`
          : url;
      handleNavigate(currurl, false);
    }

    urlsRef.current.prev = urlsRef.current.current;
    urlsRef.current.current = url;
  }, [handleNavigate]);

  useEffect(() => {
    const unsuscribe = history.listen((event, action) => {
      if (action === 'POP') {
        handleBack();
      }
    });
    return unsuscribe;
  }, [history, handleBack]);

  const renderWidget = useCallback(w => {
    const { type, ...props } = w.dataset;
    const Widget = widgetTypes[type];
    if (!Widget) {
      console.warn(
        `Widget Error: No hay ningun widget con el data-type ${type}`
      );
      return '';
    }
    return ReactDOM.createPortal(
      <ErrorBoundary onError={() => console.log(`Error en el widget ${w.id}`)}>
        <Widget {...props} id={w.id} key={w.id} widget={w} />
      </ErrorBoundary>,
      w
    );
  }, []);

  // Marcamos el hit de pagina
  useEffect(sendHit, []);

  useEffect(() => {
    window.onbeforeunload = () => {
      sessionStorage.removeItem('referrer');
    };
  });

  return (
    <AdsProvider>
      <PlayersProvider>
        <MainPlayerProvider>
          <MainPlayerContext.Consumer>
            {({ state }) => (
              <AdsController history={history}>
                <Controller
                  renderWidget={renderWidget}
                  onNavigate={handleNavigate}
                >
                  <Analytics />

                  {process.env.NODE_ENV === 'development' && (
                    <>
                      {document.getElementById('dev-header-content') &&
                        ReactDOM.createPortal(
                          <React.Suspense fallback={<div />}>
                            <Header />
                          </React.Suspense>,
                          document.getElementById('dev-header-content')
                        )}
                      <Switch>
                        <Route
                          exact
                          path="/examples"
                          component={() => (
                            <React.Suspense fallback={<div />}>
                              <Examples />
                            </React.Suspense>
                          )}
                        />
                      </Switch>{' '}
                      <Switch>
                        <Route
                          exact
                          path="/galleries"
                          component={() => (
                            <React.Suspense fallback={<div />}>
                              <Galleries />
                            </React.Suspense>
                          )}
                        />
                      </Switch>{' '}
                      {ReactDOM.createPortal(
                        <React.Suspense fallback={<div />}>
                          <Footer />
                        </React.Suspense>,
                        document.body
                      )}
                    </>
                  )}

                  {!window.location.pathname.match('/buscador') &&
                    getDeviceOS() !== 'ios' &&
                    ReactDOM.createPortal(
                      <BannerApp />,
                      document.getElementById('header')
                    )}

                  {html &&
                    ReactDOM.createPortal(
                      <Switch>
                        <Route exact path="*">
                          {/* Solo renderizamos el div si hay html, para evitar que se haga el reemplazo del innerHTML del main por alguna otra ruta y se rompa react */}
                          <BackendPage html={html} />
                        </Route>
                      </Switch>,
                      main.current
                    )}

                  {ReactDOM.createPortal(
                    <ScrollPages onNavigate={handleNavigate} />,
                    main.current.parentElement
                  )}

                  {/* Añadimos el player principal */}
                  {state.consent.answered &&
                    ReactDOM.createPortal(
                      <React.Suspense fallback={<div />}>
                        <MainPlayer />
                      </React.Suspense>,
                      document.body
                    )}
                </Controller>

                {/* Elementos que interactuan con la pagina */}
                {process.env.NODE_ENV !== 'development' && (
                  <>
                    <WidgetSticky />
                    <WidgetMenuToggle />
                    <FooterWidget />
                  </>
                )}
              </AdsController>
            )}
          </MainPlayerContext.Consumer>
          <Refresh onRefresh={handleNavigate} />
          <IntexAds />
        </MainPlayerProvider>
      </PlayersProvider>
    </AdsProvider>
  );
};

App.propTypes = {
  history: historyPropType.isRequired
};

export default withRouter(App);
